/**
 * The full "New task" form (LXP-45) — validation and the post-create plan.
 *
 * Quick Add stays the fast, title-first path. This form takes everything a task
 * can carry at birth: description, dates, estimate, people, links and files.
 * `POST /api/tasks` already accepts the text fields (`CreateTaskZ`); people,
 * links and files are separate resources that need the new task's id, so they
 * run as follow-ups after the create returns. A follow-up that fails must never
 * lose the task — the caller opens the card with a notice instead.
 *
 * Pure so it can be unit-tested; `.tsx` files are not collected by vitest.
 */

import { MAX_ATTACHMENT_BYTES } from '../../hooks/useTaskAttachments'

export interface NewTaskDraft {
  title: string
  description: string
  clientId: string
  priority: string
  discipline: string
  start: string
  due: string
  /** Free text from the input — validated into a whole number of hours. */
  estimate: string
  leadId: string
  contributorIds: string[]
  links: { url: string; label: string }[]
}

export interface CreateTaskPayload {
  title: string
  description?: string
  clientId?: string
  priority: string
  discipline: string
  start?: string
  due?: string
  estimateHrs?: number
}

export type DraftField = 'title' | 'start' | 'due' | 'estimate' | 'links'

export type DraftCheck =
  | {
      ok: true
      payload: CreateTaskPayload
      links: { url: string; label?: string }[]
      leadId: string | null
      contributorIds: string[]
    }
  | { ok: false; errors: Partial<Record<DraftField, string>> }

export function emptyDraft(defaults: Partial<NewTaskDraft> = {}): NewTaskDraft {
  return {
    title: '',
    description: '',
    clientId: '',
    priority: 'med',
    discipline: '',
    start: '',
    due: '',
    estimate: '',
    leadId: '',
    contributorIds: [],
    links: [],
    ...defaults,
  }
}

/**
 * Accept what people actually paste: `figma.com/file/x` without a scheme gets
 * `https://`. Returns null for anything that still isn't an http(s) URL — the
 * API's `z.string().url()` would reject it after the task already exists.
 */
export function normalizeUrl(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  try {
    const url = new URL(withScheme)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    if (!url.hostname.includes('.') && url.hostname !== 'localhost') return null
    return url.toString()
  } catch {
    return null
  }
}

/** Validate the draft into a create payload plus the follow-up writes. */
export function checkDraft(draft: NewTaskDraft): DraftCheck {
  const errors: Partial<Record<DraftField, string>> = {}

  const title = draft.title.trim()
  if (!title) errors.title = 'Give the task a title'

  // <input type="date"> yields YYYY-MM-DD or '', so string compare is date compare.
  if (draft.start && draft.due && draft.start > draft.due) {
    errors.due = 'Due date is before the start date'
  }

  let estimateHrs: number | undefined
  const est = draft.estimate.trim()
  if (est) {
    const n = Number(est)
    // CreateTaskZ: z.number().int().positive()
    if (!Number.isInteger(n) || n <= 0) errors.estimate = 'Whole hours, 1 or more'
    else estimateHrs = n
  }

  const links: { url: string; label?: string }[] = []
  for (const link of draft.links) {
    if (!link.url.trim() && !link.label.trim()) continue // an empty row the user never filled
    const url = normalizeUrl(link.url)
    if (!url) {
      errors.links = `"${link.url.trim() || link.label.trim()}" isn't a web address`
      break
    }
    const label = link.label.trim().slice(0, 200)
    links.push(label ? { url, label } : { url })
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors }

  const description = draft.description.trim()
  const leadId = draft.leadId || null
  return {
    ok: true,
    payload: {
      title,
      ...(description ? { description } : {}),
      ...(draft.clientId ? { clientId: draft.clientId } : {}),
      priority: draft.priority,
      discipline: draft.discipline,
      ...(draft.start ? { start: draft.start } : {}),
      ...(draft.due ? { due: draft.due } : {}),
      ...(estimateHrs !== undefined ? { estimateHrs } : {}),
    },
    links,
    leadId,
    // The lead already has a seat; never send a duplicate contributor for them.
    contributorIds: [...new Set(draft.contributorIds)].filter((id) => id && id !== leadId),
  }
}

/** Split staged files into those under the upload cap and those over it. */
export function partitionFiles<F extends { size: number }>(files: F[]): { ok: F[]; tooBig: F[] } {
  return {
    ok: files.filter((f) => f.size <= MAX_ATTACHMENT_BYTES),
    tooBig: files.filter((f) => f.size > MAX_ATTACHMENT_BYTES),
  }
}

export interface FollowUpFailure {
  kind: 'assignee' | 'link' | 'file'
  name: string
}

/**
 * The notice shown on the task card when the task was created but some of what
 * came with it wasn't. Null when everything landed.
 */
export function followUpNotice(failures: FollowUpFailure[]): string | null {
  if (failures.length === 0) return null
  const byKind = (k: FollowUpFailure['kind']) => failures.filter((f) => f.kind === k).map((f) => f.name)
  const parts: string[] = []
  const files = byKind('file')
  const links = byKind('link')
  const people = byKind('assignee')
  if (files.length) parts.push(`${files.length === 1 ? 'file' : 'files'} ${files.join(', ')}`)
  if (links.length) parts.push(`${links.length === 1 ? 'link' : 'links'} ${links.join(', ')}`)
  if (people.length) parts.push(`assigning ${people.join(', ')}`)
  return `Task created, but these didn't save: ${parts.join('; ')}. Add them again below.`
}

/** Which card tab to open on a partial failure — wherever the gap is. */
export function followUpTab(failures: FollowUpFailure[]): 'files' | 'links' | 'details' {
  if (failures.some((f) => f.kind === 'file')) return 'files'
  if (failures.some((f) => f.kind === 'link')) return 'links'
  return 'details'
}
